import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import type { CommentsCommentFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Result, UnhandledResult } from 'modules/core/lib/handleMutation'

import EmojiPicker from './EmojiPicker'
import { GroupedReaction, Reaction, ReactionsReactionValues } from './Reaction'

type ReactionsGroupedReaction = CommentsCommentFragment['groupedReactions'][number]

export interface ReactionsProps {
  items: ReactionsGroupedReaction[]
  addReaction: (variables: ReactionsReactionValues) => void
  deleteReaction: (
    id: number,
    resourceId: number
  ) => Promise<UnhandledResult<Result<Omit<unknown, 'errors'>>>>
}

interface Props {
  items: ReactionsGroupedReaction[]
  resourceId: number
  addReaction: (variables: ReactionsReactionValues) => Promise<{ id: number } | undefined>
  deleteReaction: (
    id: number,
    resourceId: number,
    textCode: string
  ) => Promise<{ success: boolean } | undefined>
}

export const Reactions = ({ items, resourceId, addReaction, deleteReaction }: Props) => {
  const { access, company } = useCurrentSession()
  const [reactions, setReactions] = useState<GroupedReaction[]>(items)

  const findReactedEmoji = (emojiId: string) => {
    return items.find((item) => {
      return item.textCode === emojiId
    })
  }

  const setReactedEmoji = (emojiId: string, id: number) => {
    setReactions((prevItems) =>
      prevItems.map((item) =>
        item.textCode === emojiId
          ? {
              ...item,
              count: item.count + 1,
              selfReactionId: id,
            }
          : item
      )
    )
  }

  const setNewEmoji = (emojiId: string, id: number) => {
    setReactions([
      ...reactions,
      {
        count: 1,
        resourceId,
        textCode: emojiId,
        selfReactionId: id,
        supportersList: [],
      },
    ])
  }

  const onAddReaction = async (emojiId: string) => {
    const result = await addReaction({
      resourceId,
      authorId: access.id,
      companyId: company.id,
      textCode: emojiId,
    })

    if (result?.id) {
      const reacted = findReactedEmoji(emojiId)

      if (reacted) {
        setReactedEmoji(emojiId, result.id)
      } else {
        setNewEmoji(emojiId, result.id)
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const onDeleteReaction = async (id: number, resourceId: number, emojiId: string) => {
    const result = await deleteReaction(id, resourceId, emojiId)

    if (result?.success) {
      setReactions((prevItems) =>
        prevItems.map((item) =>
          item.textCode === emojiId
            ? {
                ...item,
                count: item.count - 1,
                selfReactionId: null,
              }
            : item
        )
      )
    }
  }

  const onEmojiPicked = (emojiId: string) => {
    const reactedEmoji = findReactedEmoji(emojiId)

    if (reactedEmoji && reactedEmoji.selfReactionId) return

    onAddReaction(emojiId)
  }

  return (
    <Box flexDirection="row" gap="s8" flexWrap="wrap">
      {reactions.map((item) => (
        <Reaction
          key={item.textCode}
          item={item}
          resourceId={resourceId}
          addReaction={onAddReaction}
          deleteReaction={onDeleteReaction}
        />
      ))}
      <EmojiPicker onEmojiSelected={onEmojiPicked} />
    </Box>
  )
}
